import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { OpsSearchHit, searchOperations } from '../verticals/iqmax/api/ops-console';

/**
 * The operations console's header, as the prototype draws it.
 *
 * Kept apart from the family console's header rather than parameterised,
 * because they are genuinely different objects: this one carries a global
 * search and a shift indicator, that one carries a child switcher and an
 * account menu, and the only thing they share is being a bar at the top.
 *
 * The classes are the prototype's own — `.burger`, `.brand`, `.tsearch`,
 * `.shift`, `.who`. That matters more than it looks: the family header's
 * classes (`.top-l`, `.acct`, `.chip`) have **no rules at all** under
 * `.env-admin`, so the console was rendering an unstyled bar while appearing,
 * in code, to have a header.
 *
 * Source: docs/redesign/IQMaxx/4_Admin_Operaciones.html
 */

/** How long to wait after a keystroke before asking the server. */
const SEARCH_DEBOUNCE_MS = 220;

/** The prototype's own threshold; the server applies the same one. */
const MIN_QUERY = 2;

const SUBJECT_ICON: Record<string, string> = {
  case: '📋',
  audit: '📜',
  teacher: '🏫',
  family: '👪',
  learner: '🧒',
  course: '📚',
  enrollment: '🧾',
  session: '🎥',
  transaction: '💳',
};

const SUBJECT_LABEL: Record<string, string> = {
  case: 'Caso',
  audit: 'Auditoría',
  teacher: 'Profesor',
  family: 'Familia',
  learner: 'Alumno',
  course: 'Curso',
  enrollment: 'Inscripción',
  session: 'Sesión',
  transaction: 'Transacción',
};

/**
 * The header's global search — ADM-15-FR-01 and FR-02.
 *
 * Ranking, exact-identifier priority and minor-safe narrowing all happen on the
 * server. This renders what it is given and adds nothing: a client that sorted
 * or filtered would be a second opinion about what an operator may see, and the
 * requirement is that there is only one.
 */
const OpsSearch: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<OpsSearchHit[]>([]);
  const [gaps, setGaps] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [searched, setSearched] = useState(false);
  const [refused, setRefused] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (query.trim().length < MIN_QUERY) {
      setHits([]);
      setSearched(false);
      setOpen(false);
      return;
    }

    // The request in flight when the query changes again is abandoned rather
    // than rendered: without this, a slow early response lands after a fast
    // later one and the operator watches the list go backwards.
    let current = true;
    const timer = setTimeout(async () => {
      try {
        const response = await searchOperations(query);
        if (!current) return;
        setHits(response.results);
        setGaps(response.gaps ?? []);
        setRefused(response.code === 'NOT_FOUND_OR_NOT_AUTHORIZED');
      } catch {
        if (!current) return;
        setHits([]);
        setGaps([]);
        setRefused(false);
      } finally {
        if (current) {
          setSearched(true);
          setOpen(true);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [query]);

  // Clicking anywhere else closes the list, as in the prototype.
  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, []);

  const pick = useCallback(
    (hit: OpsSearchHit) => {
      setOpen(false);
      setQuery('');
      navigate(hit.route);
    },
    [navigate],
  );

  return (
    <div className="tsearch" ref={box}>
      <span aria-hidden="true">🔍</span>
      <input
        id="ops-global-search"
        type="search"
        value={query}
        placeholder="Buscar familia, profesor, clase, caso o transacción…"
        aria-label="Buscar en operaciones"
        autoComplete="off"
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => hits.length && setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'Enter' && hits.length) pick(hits[0]);
        }}
      />

      <div className={`gs-box${open ? ' show' : ''}`} role="listbox">
        {hits.map((hit) => (
          <div
            key={`${hit.subject}-${hit.id}`}
            className="gs-r"
            role="option"
            aria-selected={false}
            tabIndex={0}
            onClick={() => pick(hit)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') pick(hit);
            }}
          >
            <span className="gs-e" aria-hidden="true">
              {SUBJECT_ICON[hit.subject] ?? '•'}
            </span>
            <div>
              <b>{hit.title}</b>
              <span>
                {hit.detail}
                {/* The operator should know a name was shortened, or they will
                    go looking for a fuller record that does not exist. */}
                {hit.redacted ? ' · vista limitada' : ''}
              </span>
            </div>
            <span className="pill p-neu">{SUBJECT_LABEL[hit.subject] ?? hit.subject}</span>
          </div>
        ))}

        {searched && hits.length === 0 && refused ? (
          <div className="gs-empty">
            No encontrado o sin autorización
            <span>Ese identificador no está disponible para tu acceso</span>
          </div>
        ) : null}

        {searched && hits.length === 0 && !refused ? (
          <div className="gs-empty">
            Sin resultados para “{query}”
            <span>Prueba con un ID de caso, una familia, un profesor o una clase</span>
          </div>
        ) : null}

        {gaps.length ? (
          <div className="gs-empty">
            No se pudo leer: {gaps.join(', ')}
            <span>Esta búsqueda está incompleta</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

interface OpsHeaderProps {
  home: string;
  badge: string;
  initials: string;
  name: string;
  /** The operator's specialist role, shown under their name. */
  role: string;
  onToggleSide: () => void;
}

/**
 * The bar itself.
 *
 * The shift chip says only what is true. The prototype prints "On shift ·
 * 09:00–18:00", which is a rota this platform does not have; rendering it would
 * put a schedule on screen that nothing produced. What we do know is that this
 * operator is signed in and working, so that is what it says — and the handover
 * it implies is a real action on the queue screen.
 */
export const OpsHeader: React.FC<OpsHeaderProps> = ({
  home,
  badge,
  initials,
  name,
  role,
  onToggleSide,
}) => (
  <div className="top">
    <button className="burger" onClick={onToggleSide} title="Contraer menú" aria-label="Menú">
      ☰
    </button>

    <div className="brand">
      <Link to={home} className="logo">
        i<span>Q</span>Maxx
      </Link>
      <span className="env">{badge}</span>
    </div>

    <OpsSearch />

    <div className="top-r">
      <div className="shift">
        <i aria-hidden="true" />
        En turno
      </div>

      <Link to="/profile" className="who">
        <span className="av">{initials}</span>
        <div>
          <b>{name}</b>
          <span>{role}</span>
        </div>
      </Link>
    </div>
  </div>
);
